import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

export default function PartnershipTypes() {
  const { t } = useLanguage();

  const types = [
    {
      icon: 'ri-recycle-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      title: t('塑膠回收與造粒廠', 'Plastic Recyclers & Compounders', 'プラスチックリサイクル・造粒業者'),
      desc: t(
        '適合想提升再生料等級、建立穩定產品規格、擴大材料價值的廠商。',
        'Suitable for manufacturers looking to upgrade recycled material grades, establish stable product specifications, and expand material value.',
        '再生材料グレードの向上・安定した製品仕様の確立・材料価値の拡大を目指すメーカーに適しています。'
      ),
      tags: [t('回收廠', 'Recyclers', 'リサイクル業者'), t('造粒廠', 'Compounders', '造粒業者'), t('粉碎廠', 'Shredders', '粉砕業者')],
      benefits: [
        t('提升再生料物性與穩定性', 'Improve recycled material properties and stability', '再生材料の物性と安定性の向上'),
        t('建立可量產的材料規格', 'Establish scalable material specifications', '量産可能な材料仕様の確立'),
        t('擴大再生料市場價值', 'Expand recycled material market value', '再生材料の市場価値の拡大'),
      ],
      image: 'https://readdy.ai/api/search-image?query=plastic%20recycling%20granulation%20factory%20industrial%20machine%20recycled%20pellets%20production%20line%20green%20sustainable%20manufacturing%20facility%20professional%20industrial%20photography%20clean%20modern%20factory%20green%20accent%20high%20resolution&width=480&height=240&seq=type-recycling-v1&orientation=landscape',
      borderColor: 'border-brand-green/25',
    },
    {
      icon: 'ri-store-line',
      iconColor: 'text-brand-blue',
      iconBg: 'bg-brand-blue-light',
      title: t('經銷商與材料通路商', 'Distributors & Material Channel Partners', '販売代理店・材料流通業者'),
      desc: t(
        '適合具備工業材料、塑膠材料、回收料或 ESG 專案客戶基礎的合作夥伴。',
        'Suitable for partners with an existing customer base in industrial materials, plastic materials, recycled materials, or ESG projects.',
        '工業材料・プラスチック材料・再生材料・ESGプロジェクトの既存顧客基盤を持つパートナーに適しています。'
      ),
      tags: [t('材料經銷商', 'Material Distributors', '材料販売代理店'), t('工業材料商', 'Industrial Material Suppliers', '工業材料商'), t('產業整合商', 'Industry Integrators', '産業統合業者')],
      benefits: [
        t('授權銷售聚量完整產品線', 'Authorized sales of PolyQ\'s complete product line', '聚量の完全な製品ラインの正規販売'),
        t('技術背書與行銷素材支援', 'Technical endorsement and marketing material support', '技術的裏付けとマーケティング素材サポート'),
        t('可規劃區域獨家合作', 'Regional exclusive cooperation available', '地域独占協力の設定可能'),
      ],
      image: 'https://readdy.ai/api/search-image?query=industrial%20material%20distribution%20warehouse%20plastic%20pellets%20storage%20supply%20chain%20B2B%20professional%20photography%20clean%20modern%20facility%20blue%20accent%20lighting%20high%20resolution%20wide%20angle&width=480&height=240&seq=type-dist-v1&orientation=landscape',
      borderColor: 'border-brand-blue/25',
    },
    {
      icon: 'ri-government-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      title: t('品牌、政府與 ESG 專案單位', 'Brands, Government & ESG Project Units', 'ブランド・政府・ESGプロジェクト機関'),
      desc: t(
        '適合希望建立低碳產品、地方循環經濟示範或永續供應鏈專案的單位。',
        'Suitable for organizations looking to develop low-carbon products, local circular economy demonstrations, or sustainable supply chain projects.',
        '低炭素製品・地域サーキュラーエコノミー実証・持続可能なサプライチェーンプロジェクトの構築を目指す機関に適しています。'
      ),
      tags: [t('品牌端', 'Brand Owners', 'ブランドオーナー'), t('地方政府', 'Local Government', '地方政府'), t('ESG 顧問', 'ESG Consultants', 'ESGコンサルタント')],
      benefits: [
        t('低碳產品開發與認證支援', 'Low-carbon product development and certification support', '低炭素製品開発・認証サポート'),
        t('碳足跡與減碳數據整理', 'Carbon footprint and reduction data compilation', 'カーボンフットプリント・削減データ整理'),
        t('循環經濟示範場域建置', 'Circular economy demonstration site development', 'サーキュラーエコノミー実証サイト構築'),
      ],
      image: 'https://readdy.ai/api/search-image?query=government%20brand%20ESG%20sustainability%20project%20circular%20economy%20demonstration%20low%20carbon%20product%20professional%20photography%20clean%20modern%20environment%20green%20blue%20accent%20Taiwan%20high%20resolution&width=480&height=240&seq=type-esg-v1&orientation=landscape',
      borderColor: 'border-brand-green/25',
    },
  ];

  return (
    <section className="section-padding bg-white">
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <p className="text-[1rem] font-semibold text-brand-green uppercase tracking-widest mb-3">
            {t('夥伴類型', 'Partner Types', 'パートナータイプ')}
          </p>
          <h2
            className="font-bold text-neutral-dark mb-4"
            style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)', lineHeight: '1.4' }}
          >
            {t('合作夥伴類型', 'Partner Types', 'パートナータイプ')}
          </h2>
          <p className="text-neutral-mid max-w-xl mx-auto" style={{ fontSize: '1.1875rem', lineHeight: '1.75' }}>
            {t(
              '無論您在塑膠產業鏈的哪個位置，聚量都有對應的合作模式與資源支持。',
              'Regardless of your position in the plastic industry chain, PolyQ has corresponding cooperation models and resource support.',
              'プラスチック産業チェーンのどの立場にいても、聚量は対応する協力モデルとリソースサポートを提供します。'
            )}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {types.map((tp) => (
            <div
              key={tp.title}
              className={`group bg-white rounded-card border ${tp.borderColor} overflow-hidden flex flex-col hover:-translate-y-1 transition-all duration-250`}
              style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.07)' }}
            >
              <div className="w-full h-[180px] overflow-hidden flex-shrink-0">
                <img src={tp.image} alt={tp.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="flex flex-col flex-1 p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${tp.iconBg} flex-shrink-0`}>
                    <i className={`${tp.icon} ${tp.iconColor} text-2xl`}></i>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {tp.tags.map((tag) => (
                      <span key={tag} className={`inline-block px-2 py-0.5 rounded-full text-[0.8125rem] font-semibold ${tp.iconColor} ${tp.iconBg}`}>{tag}</span>
                    ))}
                  </div>
                </div>
                <h3 className="font-semibold text-neutral-dark mb-2 group-hover:text-brand-green transition-colors" style={{ fontSize: '1.1875rem', lineHeight: '1.5' }}>{tp.title}</h3>
                <p className="text-neutral-mid text-[1rem] leading-relaxed mb-4 flex-1">{tp.desc}</p>
                <ul className="space-y-2 mb-5">
                  {tp.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-neutral-dark text-[0.9375rem]">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-brand-green text-sm"></i>
                      </div>
                      {b}
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className="inline-flex items-center gap-1.5 text-brand-green text-[1rem] font-semibold hover:gap-2.5 transition-all duration-200 whitespace-nowrap cursor-pointer">
                  {t('了解合作方式', 'Learn About Partnership', '協力方法を知る')}
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
